<script lang="ts" context="module">

	export const KEY_AISLE = 'aisle';
	export const KEY_SUB_AISLE = 'index';

</script>
<script lang="ts">
	import { page } from '$app/stores';
	import { type AisleLayout, ProductType, sortProductsByType, type Aisle, type AisleLayoutModelBase, type LandingItemContext } from "$lib/def/landing";
	import { fetch$, type Cache } from '$lib/serv/core/cache';
	import { SPKEYS, getDB } from '$lib/serv/groc-landing';
	import { fillLayout } from '$lib/serv/groc-product';
	import { LOCATION_PROPS, toQueryString } from '$lib/serv/locations';
	import type { SideNavItem } from '$lib/serv/nav';
	import { KEY_BANNER } from '$lib/ui/home/groc/Banners.svelte';
	import { KEY_OCCASION } from '$lib/ui/home/groc/Occasions.svelte';
	import { parseObjectValuesToString, slugify } from '$lib/util/format';
	import { query } from "$lib/util/url";
	import { EMPTY, catchError, filter, map, of, shareReplay, switchMap, tap, type Observable } from 'rxjs';
	import { createEventDispatcher, getContext } from 'svelte';
	
	import Product from '$lib/ui/home/groc/Product.svelte';
	import Grid from '$lib/ui/layout/Grid.svelte';
	import Header from '$lib/ui/shared/Header.svelte';
	import CarouselV3 from '$lib/ui/layout/CarouselV3.svelte';
    import { thumbor } from '$lib/serv/image-details';

	const { landingItems, products, cacheAge } = getContext('endpoints') as LandingItemContext;

	const dispatch = createEventDispatcher();

	export let fullscreen: boolean;
	export let data: Aisle;

	let fillError: any;

	$: ([,store,,] = data?.endpoint.split('/')); // "/[store_code]/aisles/[aisle_id]"
	$: loaderIdentifiers = data ? fetchContentIndentifiers(data.endpoint, data.id) : EMPTY;
	$: loader = fullscreen ? loaderIdentifiers : EMPTY;
	$: layout = fullscreen ? extendWrapper(loaderIdentifiers) : EMPTY;

	function fetchContentIndentifiers(endpoint:string, id:number) {
		const sQuery = toQueryString(Object.fromEntries($page.url.searchParams.entries()));
		const url = new URL(sQuery !== 'lat=&lon=' ? `${landingItems + endpoint}?${sQuery}` : (landingItems + endpoint));
		const opts = { db: getDB(), msMaxCacheAge: cacheAge.medium };
		return fetch$<AisleLayoutModelBase>(url, SPKEYS, opts).pipe(
			shareReplay(1),
		);
	}

	/**
	 * TODO: we can improved performance by caching individual products.
	 * Extends the aisle stream by filling the products' with details.
	 * @param obs The empty products list stream.
	 */
	function extendWrapper(obs:Observable<Cache<AisleLayoutModelBase>>) {
		
		return obs.pipe(
			// prevent a fetch for data soon to be replaced
			filter(cache => !cache.loading && !!cache.payload),
			// drop what we don't need
			map(cache => cache.payload!),
			tap({ next: () => fillError = null }),
			switchMap(model => fillLayout(model.layout, store, products)),
			// platform's list includes dud subaisles (empty string title, empty products array)
			// i.e. ones where the store has no products for that subaisle, so filter those out
			map(res => res.layout_items?.filter((subaisle) => subaisle.products?.length > 0)),
			tap((sections) => {
				if (!sections) return;
				dispatch('nav', sections.map(a => ({
					id: a.product_selection.subaisle_id,
					title: a.title,
					setHref: (page, item) => '/?' + query(page.url)
						.toggle(KEY_SUB_AISLE, a.sort?.toString())
						// Make sure we clear out any other unrelated params, e.g. banner id
						.keep(...LOCATION_PROPS, 'vertical', 'store', KEY_AISLE, KEY_SUB_AISLE),
					active: (page, item) => {
						const q = query(page.url);
						return q.hasValue(KEY_AISLE, data.id.toString())
						&& q.hasValue(KEY_SUB_AISLE, a.sort?.toString())
						&& !(query(page.url).has(KEY_BANNER) || query(page.url).has(KEY_OCCASION)) // GW: not crazy about these two being here
					},
				} as SideNavItem)));
			}),
			catchError(er => {
				fillError = er;
				return of(null);
			})
		)
	}

function createKeyFunction<T>(index:string|null, subaisle?:AisleLayout) {
	return (payload:T) => sortProductsByType(subaisle?.products, [ProductType.Combo]);
}

</script>

{#if !fullscreen}
	<!-- Aisle tile in Groc landing items -->
	<a href={query($page.url).set(KEY_AISLE, data.id?.toString()).toUrl()} class="aisle item object-elevation-3pd">
		<img src={thumbor(data.icon, 350)} class="image" alt="icon">
		<span class="text label-m medium ellipses-2">{data.title}</span>
	</a>

{:else}
	{@const filled = $layout}
	{@const index = $page.url.searchParams.get(KEY_SUB_AISLE)}
	
	{#if index}
		<!-- See all (Sub-aisle) -->
		{@const subaisle = filled?.find(s => s.sort?.toString() === index)}
		{@const keyFunc = createKeyFunction(index, subaisle)}
		
		<Header back={`/?${query($page.url).remove(KEY_SUB_AISLE)}`} name="aisle"/>

		<Grid cache={{ loading: $loader?.loading, error: $loader?.error }} type="aisle" key={keyFunc} uniqueKey={(item) => item.catalogue_key} let:item >
			<h4 slot="title" class="headline-m bold ellipses">{subaisle?.title}</h4>
			<Product {item} on:product/>
		</Grid>
	{:else if loader}
		<!-- Aisle -->
		<!-- {@const { loading, error } = loader} instead of `payload` we use `layout` which has products -->

		<Header back={query($page.url).remove(KEY_AISLE).toUrl()}>
			<h1 class="display-l bold">{data.title}</h1>
		</Header>

		{#if $loader?.error}
			<div class="message error body-m book">
				<p class="body-m book">{$loader?.error?.friendly_message ?? $loader?.error ?? 'Unable to load aisle.'}</p>
			</div>
		{:else if fillError}
			<div class="message error body-m book">
				<p class="body-m book">{fillError?.friendly_message ?? fillError ?? 'Unable to load aisle details.'}</p>
			</div>
		<!-- {:else if $loader?.loading || !$layout}
			<h4 class="headline-m bold loading">&nbsp;</h4>
			<div class="grid aisle loading">
				{#each Array(9) as _}
					<div class="item">
						<div class="image"></div>
						<div class="text"></div>
					</div>
				{/each}
			</div> -->
		{:else}
			{@const sections = $layout}
			{#each sections ?? [] as section, index}
				<!-- `sort` value from platform is suddenly one-indexed...  Is it unsafe to rather use `title` for this comparison? -->
				<!-- <Carousel cache={{loading:false,payload:section}} type="aisle" key={payload => payload?.products} let:item>
					<div slot="title" class="flex mobile flex-nowrap title">
						<h4 id={slugify(section.title)} class="headline-m bold ellipses">{section.title}</h4>
						<a href={query($page.url).set(KEY_SUB_AISLE, section.sort.toString()).toUrl()} class="button transparent label-xl bold underlined">See all</a>
					</div>
					<Product {item} on:product/>
				</Carousel> -->
				<CarouselV3 cache={{ payload: section }} key={payload => payload?.products}>
					{#snippet title()}
						<h4 id={slugify(section.title)} class="headline-m bold ellipses m-0">{section.title}</h4>
						<a href={query($page.url).set(KEY_SUB_AISLE, section.sort.toString()).toUrl()} class="button transparent label-xl bold underlined">See all</a>
					{/snippet}
					{#snippet children({ item })}
						<Product {item} on:product/>
					{/snippet}
				</CarouselV3>
			{/each}
		{/if}
	{/if}
{/if}
